import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faSearch, faUndo, faPrint, faFileExport, faEnvelope, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/logo.png';
import careManagementService from '../../services/careManagementService';
import practiceService from '../../services/practiceService';
import { calendarAPI } from '../../services/calendarService';
import { toast } from 'react-toastify';
import html2pdf from 'html2pdf.js';
import EmailModal from '../../components/EmailModal/EmailModal';
import AddFollowUpModal from '../../components/AddFollowUpModal/AddFollowUpModal';
import Table from '../../components/Common/Table/Table';
import ListScreenPaginationBar from '../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import '../../components/Common/ListScreenPageLayout/listScreenPage.css';
import '../BillingReports/ManageBillingReport.css';
import './FollowUpQuery.css';

const FollowUpQuery = () => {
  const { user } = useSelector((state) => state.auth || {});
  const roleId = Number(user?.role_id);
  const isScopedRole = [4, 5, 7].includes(roleId); // Provider, Caregiver, System Caregiver
  const scopedPracticeId = useMemo(() => {
    const pid = user?.practice_id ?? user?.practiceId ?? user?.practices?.[0]?.id;
    return pid != null && pid !== '' ? String(pid) : '';
  }, [user?.practiceId, user?.practice_id, user?.practices]);
  const { updateBreadcrumbs } = useBreadcrumb();
  const hasInitializedRef = useRef(false);

  const [queryLoading, setQueryLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState('10');
  const [totalResults, setTotalResults] = useState(0);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [showAddFollowUpModal, setShowAddFollowUpModal] = useState(false);
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedPatientName, setSelectedPatientName] = useState('');

  // Filter states
  const defaultDateRange = roleId === 7 ? 'Last 30 days' : '';

  const [filters, setFilters] = useState({
    practiceId: '',
    patientId: '',
    dateRangeType: 'dateRange', // 'dateRange' or 'fromTo'
    dateRange: defaultDateRange,
    fromDate: '',
    toDate: '',
    providerId: '',
    practiceCaregiverId: '',
    serviceTime: '',
    systemCaregiverId: '',
    followUpStatus: ''
  });

  // Dropdown options
  const [dropdownOptions, setDropdownOptions] = useState({
    practices: [],
    patients: [],
    dateRanges: ['Last 7 days', 'Last 30 days', 'Last 90 days', 'This month', 'Last month', 'Custom'],
    providers: [],
    practiceCaregivers: [],
    serviceTimes: [],
    systemCaregivers: [],
    followUpStatuses: ['Pending', 'Completed', 'In Progress', 'Cancelled']
  });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  const isFiltersDirty = useMemo(() => {
    return (
      (!isScopedRole && filters.practiceId !== '') ||
      filters.patientId !== '' ||
      filters.dateRange !== '' ||
      filters.fromDate !== '' ||
      filters.toDate !== '' ||
      filters.providerId !== '' ||
      filters.practiceCaregiverId !== '' ||
      filters.serviceTime !== '' ||
      filters.systemCaregiverId !== '' ||
      filters.followUpStatus !== '' ||
      filters.dateRangeType !== 'dateRange'
    );
  }, [filters, isScopedRole]);

  const fuFromDateRef = useRef(null);
  const fuToDateRef = useRef(null);
  const todayDateStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (!el) return;
    try {
      el.showPicker?.();
    } catch {
      el.focus();
    }
  };

  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const baseCrumb =
      roleId === 4
        ? { label: 'Care Management', path: null }
        : roleId === 5
          ? { label: 'Caregiver Dashboard', path: '/caregiver-dashboard' }
          : roleId === 7
            ? { label: 'System Caregiver Dashboard', path: '/system-caregiver-dashboard' }
            : { label: 'Care Management', path: null };
    updateBreadcrumbs([baseCrumb, { label: 'Follow Up Query', path: null, active: true }]);

    const initializeData = async () => {
      await fetchDropdownOptions();
      if (isScopedRole && !scopedPracticeId) {
        setQueryLoading(false);
        return;
      }
      fetchFollowUpQuery(false, { silent: true });
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (!isScopedRole || !scopedPracticeId) return;
    setFilters((prev) => (
      prev.practiceId === scopedPracticeId
        ? prev
        : { ...prev, practiceId: scopedPracticeId }
    ));
  }, [isScopedRole, scopedPracticeId]);

  const prevScopedPracticeRef = useRef('');
  useEffect(() => {
    if (!hasInitializedRef.current) return;
    if (!isScopedRole || !scopedPracticeId) return;
    if (scopedPracticeId === prevScopedPracticeRef.current) return;
    prevScopedPracticeRef.current = scopedPracticeId;
    fetchFollowUpQuery(false, { silent: true });
  }, [scopedPracticeId, isScopedRole]);

  useEffect(() => {
    const pid = isScopedRole ? scopedPracticeId : filters.practiceId;
    if (pid) loadPatientsDropdown(pid);
  }, [scopedPracticeId, filters.practiceId, isScopedRole]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-dropdown')) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showExportMenu]);

  const patientsDropdownLoadedRef = useRef(new Set());

  const loadPatientsDropdown = (practiceId) => {
    if (!practiceId || patientsDropdownLoadedRef.current.has(String(practiceId))) return;
    patientsDropdownLoadedRef.current.add(String(practiceId));
    fetchPatientsForPractice(practiceId);
  };

  useEffect(() => {
    if (!filters.practiceId) {
      setDropdownOptions((prev) => ({ ...prev, patients: [] }));
    }
  }, [filters.practiceId]);

  // Fetch providers and caregivers when practice changes
  useEffect(() => {
    if (filters.practiceId) {
      fetchProvidersForPractice(filters.practiceId);
      fetchCaregiversForPractice(filters.practiceId);
    } else {
      setDropdownOptions(prev => ({
        ...prev,
        providers: [],
        practiceCaregivers: []
      }));
    }
  }, [filters.practiceId]);

  const fetchDropdownOptions = async () => {
    try {
      // Fetch practices for dropdown (minimal payload: {id, practice_name})
      if (isScopedRole) {
        setDropdownOptions(prev => ({
          ...prev,
          practices: []
        }));
      } else {
        try {
          const practicesResponse = await calendarAPI.fetchPractice();
          const practicesList = Array.isArray(practicesResponse?.practices)
            ? practicesResponse.practices
            : [];

          setDropdownOptions(prev => ({
            ...prev,
            practices: practicesList
          }));
        } catch (error) {
          console.error('Error fetching practices:', error);
          setDropdownOptions(prev => ({
            ...prev,
            practices: []
          }));
        }
      }

      // System caregivers list is only needed for admin-style filters (not for role 7 self-scope).
      if (roleId === 7) {
        setDropdownOptions((prev) => ({
          ...prev,
          systemCaregivers: []
        }));
      } else try {
        const systemCaregiversResponse = await practiceService.getSystemCaregivers();
        let caregiversList = [];
        if (systemCaregiversResponse && systemCaregiversResponse.data) {
          if (systemCaregiversResponse.data.success && systemCaregiversResponse.data.data) {
            if (systemCaregiversResponse.data.data.caregivers && Array.isArray(systemCaregiversResponse.data.data.caregivers)) {
              caregiversList = systemCaregiversResponse.data.data.caregivers;
            } else if (Array.isArray(systemCaregiversResponse.data.data)) {
              caregiversList = systemCaregiversResponse.data.data;
            }
          } else if (Array.isArray(systemCaregiversResponse.data)) {
            caregiversList = systemCaregiversResponse.data;
          }
        }
        setDropdownOptions(prev => ({
          ...prev,
          systemCaregivers: Array.isArray(caregiversList) ? caregiversList : []
        }));
      } catch (error) {
        console.error('Error fetching system caregivers:', error);
        setDropdownOptions(prev => ({
          ...prev,
          systemCaregivers: []
        }));
      }

      // Fetch follow-up query options (optional - endpoint may not exist yet)
      try {
        const optionsResponse = await careManagementService.getFollowUpQueryOptions();
        if (optionsResponse && optionsResponse.data && optionsResponse.data.success) {
          const options = optionsResponse.data.data || {};
          setDropdownOptions(prev => ({
            ...prev,
            serviceTimes: Array.isArray(options.serviceTimes) ? options.serviceTimes : [],
            dateRanges: Array.isArray(options.dateRanges) ? options.dateRanges : prev.dateRanges
          }));
        }
      } catch (error) {
        // Silently handle 404 - endpoint doesn't exist yet, use defaults
        if (error.response?.status !== 404) {
          console.warn('Error fetching follow-up query options:', error.message);
        }
        // Keep default dateRanges and empty serviceTimes if options fetch fails
      }
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    }
  };

  const fetchPatientsForPractice = async (practiceId) => {
    try {
      const response = await practiceService.getPracticePatients(practiceId, {
        page: 1,
        limit: 500,
        dropdownOnly: 1
      });
      let patientsList = [];
      if (response && response.data) {
        if (response.data.success && response.data.data) {
          if (response.data.data.patients && Array.isArray(response.data.data.patients)) {
            patientsList = response.data.data.patients;
          } else if (Array.isArray(response.data.data)) {
            patientsList = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          patientsList = response.data;
        }
      }
      setDropdownOptions(prev => ({
        ...prev,
        patients: Array.isArray(patientsList) ? patientsList : []
      }));
    } catch (error) {
      console.error('Error fetching patients:', error);
      setDropdownOptions(prev => ({
        ...prev,
        patients: []
      }));
    }
  };

  const fetchProvidersForPractice = async (practiceId) => {
    try {
      const response = await practiceService.getPracticeProviders(practiceId);
      let providersList = [];
      if (response && response.data) {
        if (response.data.success && response.data.data) {
          if (response.data.data.providers && Array.isArray(response.data.data.providers)) {
            providersList = response.data.data.providers;
          } else if (Array.isArray(response.data.data)) {
            providersList = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          providersList = response.data;
        }
      }
      setDropdownOptions(prev => ({
        ...prev,
        providers: Array.isArray(providersList) ? providersList : []
      }));
    } catch (error) {
      console.error('Error fetching providers:', error);
      setDropdownOptions(prev => ({
        ...prev,
        providers: []
      }));
    }
  };

  const fetchCaregiversForPractice = async (practiceId) => {
    try {
      const response = await practiceService.getPracticeCaregivers(practiceId);
      let caregiversList = [];
      if (response && response.data) {
        if (response.data.success && response.data.data) {
          if (response.data.data.caregivers && Array.isArray(response.data.data.caregivers)) {
            caregiversList = response.data.data.caregivers;
          } else if (Array.isArray(response.data.data)) {
            caregiversList = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          caregiversList = response.data;
        }
      }
      setDropdownOptions(prev => ({
        ...prev,
        practiceCaregivers: Array.isArray(caregiversList) ? caregiversList : []
      }));
    } catch (error) {
      console.error('Error fetching caregivers:', error);
      setDropdownOptions(prev => ({
        ...prev,
        practiceCaregivers: []
      }));
    }
  };

  const fetchFollowUpQuery = async (showToast = true, options = {}) => {
    const { silent = false, pageOverride = null, limitOverride = null } = options;
    try {
      if (!silent) {
        setQueryLoading(true);
      }

      // Build query params - only include non-empty filters
      const params = {};
      const effectivePracticeId = isScopedRole ? scopedPracticeId : filters.practiceId;
      if (isScopedRole && !effectivePracticeId) {
        setResults([]);
        setTotalResults(0);
        return;
      }
      if (effectivePracticeId) params.practiceId = effectivePracticeId;
      if (filters.patientId) params.patientId = filters.patientId;
      if (filters.dateRangeType === 'dateRange' && filters.dateRange) {
        params.dateRange = filters.dateRange;
      } else if (filters.dateRangeType === 'fromTo') {
        if (filters.fromDate) params.fromDate = filters.fromDate;
        if (filters.toDate) params.toDate = filters.toDate;
      }
      if (filters.providerId) params.providerId = filters.providerId;
      if (filters.practiceCaregiverId) params.practiceCaregiverId = filters.practiceCaregiverId;
      if (filters.serviceTime) params.serviceTime = filters.serviceTime;
      if (filters.systemCaregiverId) params.systemCaregiverId = filters.systemCaregiverId;
      if (filters.followUpStatus) params.followUpStatus = filters.followUpStatus;
      const requestedPage = pageOverride ?? currentPage;
      const requestedLimit = limitOverride ?? (entriesPerPage === 'all' || entriesPerPage === 'All' ? 9999 : (parseInt(entriesPerPage, 10) || 10));
      params.page = requestedPage;
      params.limit = requestedLimit;

      const response = await careManagementService.getFollowUpQuery(params);
      if (response.data && response.data.success) {
        const payloadData = response.data.data;
        if (Array.isArray(payloadData)) {
          setResults(payloadData);
          setTotalResults(payloadData.length);
        } else {
          setResults(payloadData?.results || payloadData?.rows || payloadData?.items || []);
          const total = payloadData?.pagination?.total ?? payloadData?.total ?? 0;
          setTotalResults(Number.isFinite(Number(total)) ? Number(total) : 0);
        }
      } else {
        if (showToast) {
          toast.error(response.data?.message || 'Failed to fetch follow-up data');
        }
        setResults([]);
        setTotalResults(0);
      }
    } catch (error) {
      console.error('Error fetching follow-up query:', error);
      if (showToast) {
        toast.error('Failed to fetch follow-up data');
      }
      setResults([]);
      setTotalResults(0);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      if (field === 'fromDate') {
        const nextFrom = value && value > todayDateStr ? todayDateStr : value;
        const next = { ...prev, fromDate: nextFrom };
        if (next.toDate && nextFrom && next.toDate < nextFrom) {
          next.toDate = nextFrom;
        }
        return next;
      }

      if (field === 'toDate') {
        const nextTo = value && value > todayDateStr ? todayDateStr : value;
        if (prev.fromDate && nextTo && nextTo < prev.fromDate) {
          return { ...prev, toDate: prev.fromDate };
        }
        return { ...prev, toDate: nextTo };
      }

      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleQuery = (e) => {
    if (e) e.preventDefault();
    setCurrentPage(1);
    fetchFollowUpQuery(false, {
      pageOverride: 1,
      limitOverride: entriesPerPage === 'all' || entriesPerPage === 'All' ? 9999 : (parseInt(entriesPerPage, 10) || 10)
    }); // manual query can show table loader
  };

  const handleReset = () => {
    setFilters({
      practiceId: isScopedRole ? scopedPracticeId : '',
      patientId: '',
      dateRangeType: 'dateRange',
      dateRange: defaultDateRange,
      fromDate: '',
      toDate: '',
      providerId: '',
      practiceCaregiverId: '',
      serviceTime: '',
      systemCaregiverId: '',
      followUpStatus: ''
    });
    setResults([]);
    setTotalResults(0);
    setCurrentPage(1);
  };

  const handlePrint = () => {
    const allFilteredResults = filteredResults;

    if (allFilteredResults.length === 0) {
      toast.warning('No data to print');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Follow Up Query Report</title>
          <style>
            @media print {
              @page {
                margin: 0.5in;
                size: letter landscape;
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 10px;
                margin: 0;
                padding: 0;
              }
              .print-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #000;
                padding-bottom: 10px;
              }
              .print-header h2 {
                margin: 0 0 5px 0;
                font-size: 18px;
              }
              .print-date {
                text-align: right;
                font-size: 10px;
                margin-bottom: 10px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 9px;
              }
              th, td {
                border: 1px solid #000;
                padding: 4px;
                text-align: left;
              }
              th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f9f9f9;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h2>Follow Up Query Report</h2>
          </div>
          <div class="print-date">Generated: ${new Date().toLocaleString()}</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Practice Name</th>
                <th>Patient Name</th>
                <th>Date and Time</th>
                <th>F/U Note Content</th>
                <th>Caregiver/Provider</th>
              </tr>
            </thead>
            <tbody>
              ${allFilteredResults.map((result, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${result.practice_name || 'N/A'}</td>
                  <td>${result.patient_name || 'N/A'}</td>
                  <td>${formatDateTime(result.date_time)}</td>
                  <td>${result.fu_note_content || 'N/A'}</td>
                  <td>${result.caregiver_provider || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div style="margin-top: 20px; font-size: 10px; text-align: center;">
            Total Records: ${allFilteredResults.length}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleExport = async (format = 'csv') => {
    const allFilteredResults = filteredResults;

    if (allFilteredResults.length === 0) {
      toast.warning('No data to export');
      return;
    }

    try {
      if (format === 'csv') {
        // Add BOM for Excel compatibility
        const BOM = '\uFEFF';
        const headers = ['#', 'Practice Name', 'Patient Name', 'Date and Time', 'F/U Note Content', 'Caregiver/Provider'];
        const rows = allFilteredResults.map((result, idx) => [
          idx + 1,
          result.practice_name || 'N/A',
          result.patient_name || 'N/A',
          formatDateTime(result.date_time),
          (result.fu_note_content || 'N/A').replace(/\n/g, ' ').replace(/\r/g, ''),
          result.caregiver_provider || 'N/A'
        ]);

        const csvContent = BOM + [
          headers.map(h => `"${h.replace(/"/g, '""')}"`).join(','),
          ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\r\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const dateStr = new Date().toISOString().split('T')[0];
        link.setAttribute('download', `Follow-Up-Query-Report-${dateStr}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Report exported to CSV successfully');
      } else if (format === 'pdf') {
        try {
          const element = document.createElement('div');
          // Hide element off-screen but keep it part of DOM for rendering
          element.style.position = 'absolute';
          element.style.left = '-9999px';
          element.style.top = '0';
          element.style.width = '11in'; // Landscape width
          element.style.zIndex = '-1';

          // Construct professional HTML structure
          element.innerHTML = `
            <div style="padding: 40px; font-family: 'Segoe UI', 'Roboto', Helvetica, Arial, sans-serif; color: #333; background: #fff;">
              
              <!-- Header -->
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; border-bottom: 2px solid #165a57; padding-bottom: 20px;">
                <div style="display: flex; align-items: center;">
                   <img src="${logo}" alt="codexterlabs" style="height: 50px; margin-right: 15px; display: block;" onerror="this.style.display='none'" />
                   <div>
                      <h1 style="margin: 0; font-size: 24px; color: #165a57; font-weight: 700; line-height: 1.2;">codexterlabs</h1>
                      <div style="font-size: 12px; color: #666;">Remote Patient Monitoring</div>
                   </div>
                </div>
                <div style="text-align: right;">
                  <h2 style="margin: 0; font-size: 20px; color: #2c3e50; font-weight: 600;">Follow Up Query Report</h2>
                  <div style="font-size: 12px; color: #666; margin-top: 5px;">Generated: ${new Date().toLocaleString()}</div>
                  <div style="font-size: 12px; color: #666;">Total Records: ${allFilteredResults.length}</div>
                </div>
              </div>

              <!-- Filter Context (Optional) -->
              ${filters.practiceId || filters.dateRangeType ? `
              <div style="margin-bottom: 20px; font-size: 11px; background: #f8f9fa; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #165a57;">
                <strong>Filters Applied:</strong>
                ${filters.practiceId ? `<span style="margin-left: 10px;">Practice ID: ${filters.practiceId}</span>` : ''}
                ${filters.dateRangeType === 'dateRange' && filters.dateRange ? `<span style="margin-left: 10px;">Range: ${filters.dateRange}</span>` : ''}
                ${filters.dateRangeType === 'fromTo' ? `<span style="margin-left: 10px;">Date: ${filters.fromDate || 'N/A'} to ${filters.toDate || 'N/A'}</span>` : ''}
              </div>
              ` : ''}

              <!-- Table -->
              <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
                <thead>
                  <tr style="background-color: #165a57; color: #ffffff;">
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; border: 1px solid #0e3c3a;">#</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; border: 1px solid #0e3c3a;">Practice Name</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; border: 1px solid #0e3c3a;">Patient Name</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; border: 1px solid #0e3c3a;">Date and Time</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; border: 1px solid #0e3c3a;">F/U Note Content</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; border: 1px solid #0e3c3a;">Caregiver/Provider</th>
                  </tr>
                </thead>
                <tbody>
                  ${allFilteredResults.map((result, idx) => `
                    <tr style="background-color: ${idx % 2 === 0 ? '#ffffff' : '#f8f9fa'};">
                      <td style="border: 1px solid #e9ecef; padding: 8px; vertical-align: top; color: #495057;">${idx + 1}</td>
                      <td style="border: 1px solid #e9ecef; padding: 8px; vertical-align: top; color: #495057; font-weight: 500;">${(result.practice_name || 'N/A').replace(/"/g, '&quot;')}</td>
                      <td style="border: 1px solid #e9ecef; padding: 8px; vertical-align: top; color: #495057;">${(result.patient_name || 'N/A').replace(/"/g, '&quot;')}</td>
                      <td style="border: 1px solid #e9ecef; padding: 8px; vertical-align: top; color: #495057; white-space: nowrap;">${formatDateTime(result.date_time)}</td>
                      <td style="border: 1px solid #e9ecef; padding: 8px; vertical-align: top; color: #495057; max-width: 350px;">
                        <div style="max-height: 100px; overflow: hidden; line-height: 1.4;">
                          ${(result.fu_note_content || 'N/A').replace(/"/g, '&quot;').replace(/\n/g, '<br>')}
                        </div>
                      </td>
                      <td style="border: 1px solid #e9ecef; padding: 8px; vertical-align: top; color: #495057;">${(result.caregiver_provider || 'N/A').replace(/"/g, '&quot;')}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>

              <!-- Footer -->
              <div style="margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; text-align: center; font-size: 9px; color: #999;">
                <p style="margin: 0;">Confidential Patient Information - © ${new Date().getFullYear()} codexterlabs. All rights reserved.</p>
                <p style="margin: 3px 0 0 0;">Generated by codexterlabs System</p>
              </div>
            </div>
          `;

          document.body.appendChild(element);

          // Wait for images to load if any
          await new Promise(resolve => setTimeout(resolve, 500));

          const opt = {
            margin: [0.3, 0.3, 0.3, 0.3], // Small margins for max content
            filename: `Follow-Up-Query-Report-${new Date().toISOString().split('T')[0]}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false, scrollY: 0 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
          };

          await html2pdf().set(opt).from(element).save();
          document.body.removeChild(element);
          toast.success('Report exported to PDF successfully');
        } catch (error) {
          console.error('Error exporting PDF:', error);
          toast.error('Failed to export PDF: ' + error.message);
        }
      }
    } catch (error) {
      console.error('Error during export:', error);
      toast.error('Export failed');
    }
  };

  const handleEmail = () => {
    const allFilteredResults = filteredResults;

    if (allFilteredResults.length === 0) {
      toast.warning('No data to email');
      return;
    }

    // Generate default content with report data
    const reportContent = generateEmailContent(allFilteredResults);
    setShowEmailModal(true);
  };

  const generateEmailContent = (data) => {
    if (!data || data.length === 0) {
      return 'No follow-up data available.';
    }

    const reportText = data.map((result, idx) => {
      return `${idx + 1}. Practice: ${result.practice_name || 'N/A'}\n   Patient: ${result.patient_name || 'N/A'}\n   Date: ${formatDateTime(result.date_time)}\n   Note: ${(result.fu_note_content || 'N/A').substring(0, 200)}${(result.fu_note_content || '').length > 200 ? '...' : ''}\n   Caregiver/Provider: ${result.caregiver_provider || 'N/A'}\n`;
    }).join('\n');

    return `Follow Up Query Report\n\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${data.length}\n\n${reportText}`;
  };

  const handleSendEmail = async (emailData) => {
    try {
      setEmailSending(true);

      // Prepare email data with current filters
      const emailPayload = {
        to: emailData.to,
        cc: emailData.cc || '',
        bcc: emailData.bcc || '',
        subject: emailData.subject,
        content: emailData.content,
        filters: {
          practiceId: filters.practiceId || null,
          patientId: filters.patientId || null,
          dateRange: filters.dateRangeType === 'dateRange' ? filters.dateRange : null,
          fromDate: filters.dateRangeType === 'fromTo' ? filters.fromDate : null,
          toDate: filters.dateRangeType === 'fromTo' ? filters.toDate : null,
          providerId: filters.providerId || null,
          practiceCaregiverId: filters.practiceCaregiverId || null,
          systemCaregiverId: filters.systemCaregiverId || null,
          serviceTime: filters.serviceTime || null,
          followUpStatus: filters.followUpStatus || null
        }
      };

      // If there are attachments, we need to use FormData
      if (emailData.attachments && emailData.attachments.length > 0) {
        const formData = new FormData();
        Object.keys(emailPayload).forEach(key => {
          if (key === 'filters') {
            formData.append(key, JSON.stringify(emailPayload[key]));
          } else {
            formData.append(key, emailPayload[key]);
          }
        });
        emailData.attachments.forEach((file) => {
          formData.append('attachments', file);
        });

        // Use axios directly for FormData
        const axios = require('axios');
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
        const response = await axios.post(
          `${API_URL}/care-management/follow-up-query/email`,
          formData,
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (response.data && response.data.success) {
          toast.success('Email sent successfully');
          setShowEmailModal(false);
        } else {
          toast.error(response.data?.message || 'Failed to send email');
        }
      } else {
        // No attachments, use regular JSON
        const response = await careManagementService.emailFollowUpQuery(emailPayload.filters, {
          to: emailPayload.to,
          cc: emailPayload.cc,
          bcc: emailPayload.bcc,
          subject: emailPayload.subject,
          content: emailPayload.content
        });

        if (response.data && response.data.success) {
          toast.success('Email sent successfully');
          setShowEmailModal(false);
        } else {
          toast.error(response.data?.message || 'Failed to send email');
        }
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email: ' + (error.response?.data?.message || error.message));
    } finally {
      setEmailSending(false);
    }
  };

  const handleAddFollowUp = (practiceId, patientId, patientName) => {
    setSelectedPracticeId(practiceId);
    setSelectedPatientId(patientId);
    setSelectedPatientName(patientName || 'Patient');
    setShowAddFollowUpModal(true);
  };

  const handleFollowUpSuccess = () => {
    // Refresh silently so query button/UI state doesn't change.
    fetchFollowUpQuery(false, { silent: true });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${month}-${day}-${year}/${String(displayHours).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Helper function to get filtered results (sorting only)
  const filteredResults = useMemo(() => {
    let filtered = [...results];

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle string comparison nicely
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue ? bValue.toLowerCase() : '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [results, sortConfig]);

  const pageSize = useMemo(() => {
    if (entriesPerPage === 'all' || entriesPerPage === 'All') {
      return Math.max(filteredResults.length, 1);
    }
    const n = parseInt(entriesPerPage, 10);
    return Number.isFinite(n) && n > 0 ? n : 10;
  }, [entriesPerPage, filteredResults.length]);

  const totalPages = Math.max(1, Math.ceil((totalResults || 0) / pageSize) || 1);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, entriesPerPage]);

  useEffect(() => {
    setCurrentPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedResults = filteredResults;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="list-screen-page-container follow-up-query-page">
      <div className="follow-up-query-inner">
        {/* Filter form — section title on border (Add Practice style) */}
        <div className="follow-up-query-section-card" role="region" aria-labelledby="follow-up-query-section-title">
          <h2 className="follow-up-query-section-header" id="follow-up-query-section-title">
            Follow Up Query
          </h2>
          <form className="follow-up-query-form" onSubmit={handleQuery}>
            <div className="follow-up-query-form-grid">
              <div className="fuq-field fuq-span-2">
                <label htmlFor="fuq-practice">Select Practice</label>
                <select
                  id="fuq-practice"
                  className="form-select-custom"
                  value={filters.practiceId}
                  onChange={(e) => handleFilterChange('practiceId', e.target.value)}
                  disabled={isScopedRole}
                >
                  <option value="">-- Select --</option>
                  {Array.isArray(dropdownOptions.practices) && dropdownOptions.practices
                    .filter((practice, index, self) =>
                      index === self.findIndex(p => p.id === practice.id)
                    )
                    .map((practice, idx) => (
                      <option key={`practice-${practice.id}-${idx}`} value={practice.id}>
                        {practice.practice_name}
                      </option>
                    ))}
                  {/* If scoped role and practice list is empty, show the scoped practice as an option */}
                  {isScopedRole && dropdownOptions.practices.length === 0 && scopedPracticeId && (
                    <option value={scopedPracticeId}>
                      {user?.practice_name || user?.practice || 'My Practice'}
                    </option>
                  )}
                </select>
              </div>

              <div className="fuq-field fuq-span-2">
                <label htmlFor="fuq-provider">Select Provider</label>
                <select
                  id="fuq-provider"
                  className="form-select-custom"
                  value={filters.providerId}
                  onChange={(e) => handleFilterChange('providerId', e.target.value)}
                  disabled={!(isScopedRole ? scopedPracticeId : filters.practiceId)}
                >
                  <option value="">-- Select --</option>
                  {Array.isArray(dropdownOptions.providers) && dropdownOptions.providers
                    .filter((provider, index, self) =>
                      index === self.findIndex(p => p.id === provider.id)
                    )
                    .map((provider, idx) => (
                      <option key={`provider-${provider.id}-${idx}`} value={provider.id}>
                        {provider.first_name} {provider.last_name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="fuq-field fuq-span-2">
                <label htmlFor="fuq-patient">Select Patient</label>
                <select
                  id="fuq-patient"
                  className="form-select-custom"
                  value={filters.patientId}
                  onChange={(e) => handleFilterChange('patientId', e.target.value)}
                  onFocus={() => {
                    const pid = isScopedRole ? scopedPracticeId : filters.practiceId;
                    if (pid) loadPatientsDropdown(pid);
                  }}
                  disabled={!(isScopedRole ? scopedPracticeId : filters.practiceId)}
                >
                  <option value="">-- Select --</option>
                  {Array.isArray(dropdownOptions.patients) && dropdownOptions.patients
                    .filter((patient, index, self) =>
                      index === self.findIndex(p => p.id === patient.id)
                    )
                    .map((patient, idx) => (
                      <option key={`patient-${patient.id}-${idx}`} value={patient.id}>
                        {patient.last_name}, {patient.first_name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="fuq-field">
                <label htmlFor="fuq-pc">Select Practice Caregiver</label>
                <select
                  id="fuq-pc"
                  className="form-select-custom"
                  value={filters.practiceCaregiverId}
                  onChange={(e) => handleFilterChange('practiceCaregiverId', e.target.value)}
                  disabled={!(isScopedRole ? scopedPracticeId : filters.practiceId)}
                >
                  <option value="">-- Select --</option>
                  {Array.isArray(dropdownOptions.practiceCaregivers) && dropdownOptions.practiceCaregivers
                    .filter((caregiver, index, self) =>
                      index === self.findIndex(c => c.id === caregiver.id)
                    )
                    .map((caregiver, idx) => (
                      <option key={`practice-caregiver-${caregiver.id}-${idx}`} value={caregiver.id}>
                        {caregiver.first_name} {caregiver.last_name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="fuq-field">
                <label htmlFor="fuq-sc">Select System Caregiver</label>
                <select
                  id="fuq-sc"
                  className="form-select-custom"
                  value={filters.systemCaregiverId}
                  onChange={(e) => handleFilterChange('systemCaregiverId', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {Array.isArray(dropdownOptions.systemCaregivers) && dropdownOptions.systemCaregivers
                    .filter((caregiver, index, self) =>
                      index === self.findIndex(c => c.id === caregiver.id)
                    )
                    .map((caregiver, idx) => (
                      <option key={`system-caregiver-${caregiver.id}-${idx}`} value={caregiver.id}>
                        {caregiver.first_name} {caregiver.last_name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="fuq-date-meta-row">
                <div className="fuq-field fuq-date-block-inner">
                  <label className="fuq-top-label" htmlFor="fuq-date-range-select">
                    Date Range
                  </label>
                  <div className="fuq-date-preset-row">
                    <input
                      id="fuq-dr-preset"
                      type="radio"
                      name="dateRangeType"
                      value="dateRange"
                      className="fuq-radio"
                      aria-label="Enable date range filter"
                      checked={filters.dateRangeType === 'dateRange'}
                      onChange={(e) => handleFilterChange('dateRangeType', e.target.value)}
                    />
                    <select
                      id="fuq-date-range-select"
                      className="form-select-custom fuq-date-range-select"
                      value={filters.dateRange}
                      onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                      disabled={filters.dateRangeType !== 'dateRange'}
                    >
                      <option value="">-- Select --</option>
                      {Array.isArray(dropdownOptions.dateRanges) && dropdownOptions.dateRanges.map(range => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="fuq-field">
                  <label htmlFor="fuq-service">Select Service Time</label>
                  <select
                    id="fuq-service"
                    className="form-select-custom"
                    value={filters.serviceTime}
                    onChange={(e) => handleFilterChange('serviceTime', e.target.value)}
                  >
                    <option value="">-- Select --</option>
                    {Array.isArray(dropdownOptions.serviceTimes) && dropdownOptions.serviceTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div className="fuq-field">
                  <label htmlFor="fuq-status">Select Follow Up Status</label>
                  <select
                    id="fuq-status"
                    className="form-select-custom"
                    value={filters.followUpStatus}
                    onChange={(e) => handleFilterChange('followUpStatus', e.target.value)}
                  >
                    <option value="">-- Select --</option>
                    {Array.isArray(dropdownOptions.followUpStatuses) && dropdownOptions.followUpStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="fuq-from-to-actions-row">
                <div className="fuq-field fuq-from-to-cell">
                  <label className="fuq-top-label" htmlFor="fuq-from-date">
                    From
                  </label>
                  <div className="fuq-from-to-row">
                    <input
                      id="fuq-dr-fromto"
                      type="radio"
                      name="dateRangeType"
                      value="fromTo"
                      className="fuq-radio"
                      aria-label="Enable from and to date filter"
                      checked={filters.dateRangeType === 'fromTo'}
                      onChange={(e) => handleFilterChange('dateRangeType', e.target.value)}
                    />
                    <div className="mbr-date-field fuq-mbr-date">
                      <input
                        id="fuq-from-date"
                        ref={fuFromDateRef}
                        type="date"
                        className="form-input-custom mbr-date-input"
                        value={filters.fromDate}
                        max={todayDateStr}
                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                        disabled={filters.dateRangeType !== 'fromTo'}
                        onClick={(e) => {
                          if (filters.dateRangeType !== 'fromTo') return;
                          try {
                            e.currentTarget.showPicker?.();
                          } catch {
                            /* ignore */
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="mbr-date-calendar-btn"
                        aria-label="Open from date calendar"
                        disabled={filters.dateRangeType !== 'fromTo'}
                        onClick={() => openDatePicker(fuFromDateRef)}
                      >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </button>
                    </div>
                    <span className="fuq-from-to-sep">To</span>
                    <div className="mbr-date-field fuq-mbr-date">
                      <input
                        id="fuq-to-date"
                        ref={fuToDateRef}
                        type="date"
                        className="form-input-custom mbr-date-input"
                        value={filters.toDate}
                        min={filters.fromDate || undefined}
                        max={todayDateStr}
                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                        disabled={filters.dateRangeType !== 'fromTo'}
                        onClick={(e) => {
                          if (filters.dateRangeType !== 'fromTo') return;
                          try {
                            e.currentTarget.showPicker?.();
                          } catch {
                            /* ignore */
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="mbr-date-calendar-btn"
                        aria-label="Open to date calendar"
                        disabled={filters.dateRangeType !== 'fromTo'}
                        onClick={() => openDatePicker(fuToDateRef)}
                      >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="fuq-field fuq-actions-cell">
                  <div className="form-actions fuq-form-actions">
                    <div className="actions-right">
                      <button type="submit" className="query-btn">
                        <FontAwesomeIcon icon={faSearch} /> Query
                      </button>
                      <button
                        type="button"
                        className="reset-btn"
                        onClick={handleReset}
                        disabled={emailSending}
                      >
                        <FontAwesomeIcon icon={faUndo} /> Reset
                      </button>
                      <button type="button" className="print-btn" onClick={handlePrint} disabled={emailSending}>
                        <FontAwesomeIcon icon={faPrint} /> Print
                      </button>
                      <div className="export-dropdown">
                        <button
                          type="button"
                          className="export-btn"
                          onClick={() => setShowExportMenu(!showExportMenu)}
                          disabled={emailSending}
                        >
                          <FontAwesomeIcon icon={faFileExport} /> Export <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', marginLeft: '2px' }} />
                        </button>
                        <div className={`export-menu ${showExportMenu ? 'show' : ''}`}>
                          <button
                            type="button"
                            onClick={() => {
                              setShowExportMenu(false);
                              setTimeout(() => handleExport('csv'), 100);
                            }}
                          >
                            Export to CSV
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowExportMenu(false);
                              setTimeout(() => handleExport('pdf'), 100);
                            }}
                          >
                            Export to PDF
                          </button>
                        </div>
                      </div>
                      <button type="button" className="email-btn" onClick={handleEmail} disabled={emailSending}>
                        <FontAwesomeIcon icon={faEnvelope} /> Email
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Results Table */}
        <div className="follow-up-query-table-section">
          <div className="table-wrap">
            <Table
              columns={[
                { key: 'practice_name', title: 'Practice Name', sortable: true, render: (row) => row.practice_name || 'N/A' },
                { key: 'patient_name', title: 'Patient Name', sortable: true, render: (row) => row.patient_name || 'N/A' },
                { key: 'date_time', title: 'Date and Time', sortable: true, render: (row) => formatDateTime(row.date_time) },
                { key: 'service_time', title: 'Service Time (min)', sortable: true, render: (row) => String(row.service_time ?? 0) },
                {
                  key: 'fu_note_content',
                  title: 'F/U Note Content',
                  sortable: true,
                  render: (row) => <div className="note-content-cell">{row.fu_note_content || 'N/A'}</div>,
                },
                { key: 'caregiver_provider', title: 'Caregiver/Provider', sortable: true, render: (row) => row.caregiver_provider || 'N/A' },
                {
                  key: 'action',
                  title: 'Action',
                  className: 'reusable-table-centered-col',
                  render: (row) => (
                    <button
                      className="add-fu-btn"
                      onClick={() => handleAddFollowUp(row.practice_id, row.patient_id, row.patient_name)}
                    >
                      Add f/u
                    </button>
                  ),
                },
              ]}
              data={paginatedResults}
              sortBy={sortConfig.key}
              sortOrder={sortConfig.direction === 'ascending' ? 'asc' : 'desc'}
              onSort={requestSort}
              isLoading={queryLoading}
              loadingMode="skeleton"
              skeletonRows={Math.min(pageSize || 10, 10)}
              emptyMessage="No data available in table"
            />
          </div>

          {filteredResults.length > 0 && (
            <ListScreenPaginationBar
              entriesValue={entriesPerPage === 'all' ? 'all' : String(entriesPerPage)}
              onEntriesChange={(value) => {
                let nextEntries = value;
                if (value === 'all') {
                  setEntriesPerPage('all');
                  nextEntries = 'all';
                } else {
                  setEntriesPerPage(String(value));
                  nextEntries = String(value);
                }
                setCurrentPage(1);
                fetchFollowUpQuery(false, {
                  pageOverride: 1,
                  limitOverride: nextEntries === 'all' || nextEntries === 'All' ? 9999 : (parseInt(nextEntries, 10) || 10)
                });
              }}
              showPageSelector={entriesPerPage !== 'all' && entriesPerPage !== 'All'}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                handlePageChange(page);
                fetchFollowUpQuery(false, {
                  pageOverride: page,
                  limitOverride: entriesPerPage === 'all' || entriesPerPage === 'All' ? 9999 : (parseInt(entriesPerPage, 10) || 10)
                });
              }}
            />
          )}
        </div>
      </div>

      {/* Email Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        defaultSubject={`Follow Up Query Report - ${new Date().toLocaleDateString()}`}
        defaultContent={generateEmailContent(filteredResults)}
      />

      {/* Add Follow Up Modal */}
      <AddFollowUpModal
        isOpen={showAddFollowUpModal}
        onClose={() => {
          setShowAddFollowUpModal(false);
          setSelectedPracticeId(null);
          setSelectedPatientId(null);
          setSelectedPatientName('');
        }}
        practiceId={selectedPracticeId}
        patientId={selectedPatientId}
        patientName={selectedPatientName}
        onSuccess={handleFollowUpSuccess}
      />
    </div>
  );
};

export default FollowUpQuery;

